import { ICareerSkillNode } from 'core/hooks/CareerSkillGraph/transform/transformCareerSkillGraph';

import { ISkillGraphIndex, clusterProgress } from './skillGraphModel';

/**
 * Координаты узлов карты навыков (ТЗ 0070).
 *
 * 🔴 ЕДИНСТВЕННЫЙ источник координат. Правило раскладки, живущее внутри
 * компонента, проверить нечем — а здесь оно чистая функция, и приёмочное
 * требование «одинаковые данные дают одинаковые координаты» проверяется одной
 * строкой: перемешать вход, разложить дважды, сравнить.
 *
 * Четыре свойства держат эту воспроизводимость, и каждое — это способ её
 * молча сломать:
 *
 * 1. **Порядок массивов сервера не принимается на веру.** Всё пересортировано
 *    по `(order, стабильный ключ)`. Смени бэкенд свой `ORDER BY` — узлы не
 *    поедут.
 * 2. 🔴 **Никакого `localeCompare`.** Он сортирует по таблицам ICU, а те
 *    отличаются между сборками — и одна и та же карта разъехалась бы между
 *    машиной разработчика и телефоном студента.
 * 3. **Никакой обратной связи от измерений.** Коробки узлов константны, текст
 *    обрезается CSS. Стоит начать мерить ширину подписи — раскладка станет
 *    зависеть от того, какие шрифты успели загрузиться, и станет непроверяемой
 *    в jsdom, где `getBBox` не существует вовсе.
 * 4. **Ни `Math.random`, ни `Date`.** Смещение параллельных рёбер берётся из
 *    детерминированного хеша пары идентификаторов.
 *
 * Раскладка слева направо, предков на холсте НЕТ: при горизонтальной
 * компоновке фокус — левый край, и колонки предков съели бы ширину, которая
 * нужна содержимому. Предки живут в хлебных крошках.
 */

/**
 * 🔴 Ширина карточки навыка выведена АРИФМЕТИКОЙ КОРОБКИ, а не на глаз:
 *
 *     12 (отступ) + 24 (знак) + 8 + 176 (имя и строка статуса) + 12 = 232
 *
 * ТЗ 0071: дробь «3 / 4» справа от имени заменена второй строкой статуса
 * словами. 176 = самая длинная польская строка «Poziom 3 → wymagany 4»
 * (164 px, замерено `getComputedTextLength` Rubik 14 на стенде) + 12 px
 * затухания края. Русские строки холста укорочены до той же ширины
 * («Получить · уровень 3»), а полная фраза уровня — в паспорте, в списке и в
 * доступном имени карточки.
 *
 * Колонка навыков стоит на 564, следующая — на 844: 564 + 232 = 796 оставляет
 * 48 px под рёбра.
 *
 * ⚠️ Мерить ширину на лету нельзя: раскладка станет зависеть от того, какие
 * шрифты успели загрузиться, и перестанет проверяться в jsdom. Число считается
 * вручную и пересчитывается ВРУЧНУЮ при каждой смене кегля или подписи.
 */
export const NODE_W = 232;
/**
 * 🔴 ТЗ 0071: карточка навыка ДВУХСТРОЧНАЯ, той же высоты, что группа.
 *
 * Однострочная (52) держала справа дробь «5 / 3» шириной 32 px, и проверка в
 * Brave показала, что дробь не читается: непонятно, где чей уровень. Фраза
 * «Poziom 2 → wymagany 3» в 32 px не входит, поэтому статус переехал второй
 * строкой под имя — тем же приёмом, что заголовок и «5 из 12» у группы.
 */
export const NODE_H = 60;
export const CLUSTER_W = 220;
/**
 * 🔴 Явные высоты группы и профессии, а не умолчание `h: NODE_H`.
 *
 * Карточка группы ДВУХСТРОЧНАЯ (заголовок и «5 из 12»), и через умолчание она
 * молча схлопнулась бы вместе с карточкой навыка — вторая строка оказалась бы
 * прижата к нижней рамке. Один тихий канал, по которому чужое решение утекает в
 * соседний вид карточки, стоит двух явных констант.
 *
 * В одну строку группу не свести: заголовок «Automatyzacja i CI/CD» занимает
 * 170 px, и однострочная карточка вышла бы ШИРЕ нынешней — прямо против задачи.
 */
export const CLUSTER_H = 60;
export const TRACK_W = 208;
export const TRACK_H = 52;
/** Заглушка стоит В КОЛОНКЕ НАВЫКОВ и обязана быть её ширины. */
export const MORE_W = NODE_W;
export const MORE_H = 52;

/** Шаг между колонками и между соседними строками. */
export const COLUMN_X = [0, 276, 564, 844];
export const SLOT_Y = 68;
export const STAGE_PAD = 24;

/**
 * Геометрия ВНУТРИ карточки. Живёт здесь, рядом с размерами коробки, а не в
 * разметке холста: числа выведены друг из друга, и разнесённые по двум файлам
 * они разъезжаются на первой же смене кегля.
 *
 * Базовая линия единственной строки: отступ (52 − 23.2) / 2 = 14.4, плюс
 * 0.711 em высоты заглавной при кегле 16 — оптический центр даёт 32.
 * Двухстрочная группа сохраняет прежнюю пару линий 24 и 45.
 */
export const CARD_PAD_X = 12;
export const GLYPH_SIZE = 24;
export const TEXT_X = 44;
export const TEXT_GAP = 8;
/**
 * Имя и строка статуса занимают всю ширину справа от знака: дроби уровней
 * справа больше нет (ТЗ 0071).
 */
export const NAME_W = NODE_W - TEXT_X - CARD_PAD_X;
export const LINE_BASELINE = 32;
export const TITLE_BASELINE = 24;
export const META_BASELINE = 45;
export const CARD_RX = 12;
export const RING_CX = 25;
export const RING_R = 11;
export const RING_GLYPH = 12;

/**
 * Ширины обрезки текста.
 *
 * 🔴 Записаны как АРИФМЕТИКА КОРОБКИ, а не числами: тогда тест проверяет
 * тождество («имя плюс зазор плюс уровни плюс отступ равны ширине карточки»), а
 * не совпадение с константой, и правка одной величины ловится, а не
 * подтверждается.
 *
 * Обрезка нужна потому, что у SVG-текста её нет: `text-overflow` к `<text>` не
 * применяется, и длинное имя молча рисуется поверх рамки. Резать по числу
 * символов нельзя — это догадка о пикселях: «Illinois» и «WWWWWWWW» при равной
 * длине отличаются вдвое.
 */
export const NAME_CLIP_W = TEXT_X + NAME_W;
export const CLUSTER_CLIP_W = CLUSTER_W - CARD_PAD_X;
export const TRACK_CLIP_W = TRACK_W - CARD_PAD_X;
export const MORE_CLIP_W = MORE_W - 2 * CARD_PAD_X;

/**
 * Границы масштаба.
 *
 * 🔴 `MIN_ZOOM` ВЫВЕДЕН из зоны нажатия, а не подобран: 44 / 52 = 0.846 →
 * округление вверх до 0.85. Инвариант — «44 CSS-пикселя»; высота карточки —
 * проектное решение; нижняя граница масштаба — следствие. Ужимать карточку и
 * оставлять прежние 0.8 значило бы отдать 44 ради вида.
 *
 * Побочно оба пола читаемости стали ВЫШЕ прежних: имя 16 · 0.85 = 13.6 px
 * (было 12.8), уровни 14 · 0.85 = 11.9 (было 11.2).
 */
export const MIN_ZOOM = 0.85;
export const MAX_ZOOM = 2;

/**
 * Потолок кнопки «Вписать».
 *
 * 🔴 Вписывание ТОЛЬКО УМЕНЬШАЕТ. Это свойство операции, а не подобранное
 * число: сцена уже холста не имеет права раздуваться, иначе на широком мониторе
 * карта превращается в три карточки во весь экран.
 */
export const FIT_ZOOM_CEILING = 1;

/**
 * Сколько узлов колонки показываем. Единое `k` на ВСЮ колонку, не на кластер:
 * покажи у одной группы шесть навыков, а у соседней девять — и читатель примет
 * разницу за свойство данных, а не за нехватку места.
 *
 * 9, а не 8: ряд подешевел по высоте ещё раз (68 против 76), и выигрыш
 * потрачен на содержимое, а не на укорочение ленты — жалоба владельца была про
 * плотность информации, а не про длину прокрутки.
 */
export const COLUMN_LIMIT = 9;

export type SkillCardKind = 'track' | 'cluster' | 'skill' | 'more';

export interface IPlacedCard {
  key: string;
  kind: SkillCardKind;
  level: number;
  x: number;
  y: number;
  w: number;
  h: number;
  label: string;
  /** Только у `skill`. */
  node: ICareerSkillNode | null;
  /** Только у `cluster`: подпись «5 z 12» (+ «+2 dodatkowe»). */
  done: number;
  total: number;
  optional: number;
  /** Только у `more`: идентификаторы спрятанных узлов. */
  hidden: string[];
  /** Родитель внутри ветки — по нему рисуется иерархическое ребро. */
  parentKey: string | null;
}

export interface ISkillLayout {
  cards: IPlacedCard[];
  byKey: Map<string, IPlacedCard>;
  width: number;
  height: number;
}

/**
 * Глубина показа: только группы или группы с навыками.
 *
 * 🔴 Третьего значения больше нет. Прежняя «3» не была глубиной: единственное,
 * чем она отличалась от «2», — включала колонку замыкания зависимостей, а ею и
 * так ведает отдельный переключатель. Две кнопки из трёх рисовали одно и то же,
 * и человек честно не понимал, что нажал.
 */
export type SkillMapDepth = 1 | 2;

export interface ILayoutOptions {
  depth: SkillMapDepth;
  /** Показывать ли колонку зависимостей (глубина 3). */
  deps: boolean;
  /** Выбранный навык: он ВСЕГДА втягивается в видимый набор. */
  selected: string | null;
  /**
   * Какие группы раскрыты (ТЗ 0071, п. 6.1).
   *
   * Не задано или `null` — решает глубина: на 2 раскрыты все группы с обрезкой
   * колонки, на 1 — ни одной. Массив — раскрыты ровно эти группы, остальные
   * стоят свёрнутыми карточками на своих местах.
   *
   * 🔴 Свёрнутые группы ОСТАЮТСЯ на холсте. Прежний `focus` выбрасывал соседей
   * целиком, и человек, раскрывший одну группу, терял из виду, из чего
   * профессия вообще состоит — ровно то, что стартовый вид ТЗ 0071 должен
   * показывать первым.
   *
   * 🔴 Раскрытая по списку группа показывается ЦЕЛИКОМ, без заглушки «ещё N».
   * Обрезка нужна, когда раскрыто всё и лента иначе бесконечна; одну группу
   * резать незачем — человек раскрыл её, чтобы увидеть.
   */
  expanded?: string[] | null;
}

const card = (over: Partial<IPlacedCard> & Pick<IPlacedCard, 'key' | 'kind' | 'level'>) =>
  ({
    x: 0,
    y: 0,
    w: NODE_W,
    h: NODE_H,
    label: '',
    node: null,
    done: 0,
    total: 0,
    optional: 0,
    hidden: [],
    parentKey: null,
    ...over,
  } as IPlacedCard);

/**
 * Скрыть РОВНО ОДИН узел нельзя.
 *
 * Заглушка «Jeszcze N umiejętności» займёт ту же строку, что занял бы
 * спрятанный узел: колонка не станет короче ни на пиксель, зато потеряет
 * навык. Поэтому при остатке в единицу показываем всё.
 */
export const shownCount = (total: number, limit: number): number =>
  total <= limit + 1 ? total : limit;

/**
 * Какие узлы колонки видны.
 *
 * Выбранный навык втягивается всегда, вытесняя последний по порядку: иначе
 * `?skill=<slug>` мог бы указывать на узел, которого на карте нет, и паспорт
 * открывался бы рядом с пустым местом.
 */
export const visibleNodes = (
  nodes: ICareerSkillNode[],
  limit: number,
  selected: string | null,
): { shown: ICareerSkillNode[]; hidden: ICareerSkillNode[] } => {
  const count = shownCount(nodes.length, limit);
  const shown = nodes.slice(0, count);
  const hidden = nodes.slice(count);
  if (!selected) return { shown, hidden };

  const index = hidden.findIndex((node) => node.id === selected || node.slug === selected);
  if (index < 0) return { shown, hidden };

  const promoted = hidden[index];
  const demoted = shown[shown.length - 1];
  return {
    shown: [...shown.slice(0, -1), promoted],
    hidden: [...hidden.slice(0, index), demoted, ...hidden.slice(index + 1)],
  };
};

/**
 * Пост-порядковая раскладка.
 *
 * 🔴 Лист занимает следующий свободный слот поперечной оси, а внутренний узел
 * встаёт ПОСЕРЕДИНЕ МЕЖДУ ПЕРВЫМ И ПОСЛЕДНИМ РЕБЁНКОМ ПО КООРДИНАТЕ, а не по
 * номерам слотов. Разница видна только на неровных ветках — и именно поэтому у
 * неё отдельный тест: глазами такое не проверяется, а ребро, уходящее мимо
 * середины группы, читается как ошибка данных.
 */
export const layoutSkillGraph = (
  index: ISkillGraphIndex,
  options: ILayoutOptions,
): ISkillLayout => {
  const { depth, deps, selected } = options;
  const expanded = options.expanded ?? null;
  const cards: IPlacedCard[] = [];
  let slot = 0;

  const { clusters } = index;
  const isOpen = (code: string) => (expanded === null ? depth === 2 : expanded.includes(code));
  const limit = expanded === null ? COLUMN_LIMIT : Number.POSITIVE_INFINITY;

  const trackCard = card({
    key: 'track',
    kind: 'track',
    level: 0,
    w: TRACK_W,
    h: TRACK_H,
    label: index.trackTitle,
  });

  const clusterCards: IPlacedCard[] = [];
  clusters.forEach((cluster) => {
    const nodes = index.nodesOf.get(cluster.code) || [];
    const progress = clusterProgress(index, cluster.code);
    const clusterCard = card({
      key: `cluster:${cluster.code}`,
      kind: 'cluster',
      level: 1,
      w: CLUSTER_W,
      // Высота ЯВНАЯ: группа двухстрочная, и умолчание `h: NODE_H` схлопнуло бы
      // её вместе с карточкой навыка.
      h: CLUSTER_H,
      label: cluster.title,
      done: progress.done,
      total: progress.total,
      optional: progress.optional,
      parentKey: 'track',
    });

    if (!isOpen(cluster.code) || !nodes.length) {
      clusterCard.y = slot * SLOT_Y;
      slot += 1;
      clusterCards.push(clusterCard);
      cards.push(clusterCard);
      return;
    }

    const { shown, hidden } = visibleNodes(nodes, limit, selected);
    const children: IPlacedCard[] = [];
    shown.forEach((node) => {
      const nodeCard = card({
        key: node.id,
        kind: 'skill',
        level: 2,
        label: node.name,
        node,
        parentKey: clusterCard.key,
        y: slot * SLOT_Y,
      });
      slot += 1;
      children.push(nodeCard);
      cards.push(nodeCard);
    });
    if (hidden.length) {
      const more = card({
        key: `more:${cluster.code}`,
        kind: 'more',
        level: 2,
        w: MORE_W,
        h: MORE_H,
        hidden: hidden.map((node) => node.id),
        parentKey: clusterCard.key,
        y: slot * SLOT_Y,
      });
      slot += 1;
      children.push(more);
      cards.push(more);
    }

    // Середина между ПЕРВЫМ и ПОСЛЕДНИМ ребёнком по координате.
    const first = children[0];
    const last = children[children.length - 1];
    clusterCard.y = (first.y + last.y + last.h - clusterCard.h) / 2;
    clusterCards.push(clusterCard);
    cards.push(clusterCard);
  });

  if (clusterCards.length) {
    const first = clusterCards[0];
    const last = clusterCards[clusterCards.length - 1];
    trackCard.y = (first.y + last.y + last.h - trackCard.h) / 2;
  }
  cards.push(trackCard);

  // Четвёртая колонка: замыкание зависимостей видимой ветки. Только при
  // включённом переключателе — иначе это ещё двадцать коробок на экране,
  // которых никто не просил. Отдельного условия на глубину не нужно: на
  // глубине 1 карточек навыков нет вовсе, и замыкать нечего.
  if (deps) {
    const visible = new Set(cards.filter((row) => row.kind === 'skill').map((row) => row.key));
    const extra: string[] = [];
    cards
      .filter((row) => row.kind === 'skill')
      .forEach((row) => {
        (index.opensOut.get(row.key) || []).forEach((edge) => {
          if (visible.has(edge.to) || extra.includes(edge.to)) return;
          extra.push(edge.to);
        });
      });
    extra.forEach((id) => {
      const node = index.byId.get(id);
      if (!node) return;
      cards.push(
        card({
          key: id,
          kind: 'skill',
          level: 3,
          label: node.name,
          node,
          y: slot * SLOT_Y,
        }),
      );
      slot += 1;
    });
  }

  cards.forEach((row) => {
    row.x = (COLUMN_X[row.level] ?? COLUMN_X[COLUMN_X.length - 1]) + STAGE_PAD;
    row.y += STAGE_PAD;
  });

  const byKey = new Map(cards.map((row) => [row.key, row]));
  const width = cards.reduce((max, row) => Math.max(max, row.x + row.w), 0) + STAGE_PAD;
  const height = cards.reduce((max, row) => Math.max(max, row.y + row.h), 0) + STAGE_PAD;
  return { cards, byKey, width, height };
};
